import AppBuilderContainerComponent from "@AppBuilderShared/components/shapediver/appbuilder/AppBuilderContainerComponent";
import ViewportAnchor2d from "@AppBuilderShared/components/shapediver/viewport/anchors/ViewportAnchor2d";
import ViewportAnchor3d from "@AppBuilderShared/components/shapediver/viewport/anchors/ViewportAnchor3d";
import {
	IAppBuilderContainer,
	isAnchor2dContainer,
	isAnchor3dContainer,
} from "@AppBuilderShared/types/shapediver/appbuilder";
import React, {useEffect, useState} from "react";

interface Props {
	namespace: string;
	containers: IAppBuilderContainer[] | undefined;
}

/**
 * Custom hook to generate viewport anchors from app builder containers.
 *
 * @param props - The properties containing namespace and containers.
 * @returns An array of JSX elements representing the viewport anchors.
 */
export function useViewportAnchors(props: Props): JSX.Element[] {
	const {namespace, containers} = props;

	const [anchors, setAnchors] = useState<JSX.Element[]>([]);

	useEffect(() => {
		const anchors: JSX.Element[] = [];
		containers?.forEach((container) => {
			if (isAnchor3dContainer(container)) {
				anchors.push(
					<ViewportAnchor3d
						key={JSON.stringify(container)}
						id={container.props.id}
						location={container.props.location}
						justification={container.props.justification}
						allowPointerEvents={
							container.props.allowPointerEvents ?? true
						}
						element={
							<AppBuilderContainerComponent
								namespace={namespace}
								{...container}
							/>
						}
						previewIcon={container.props.previewIcon}
						width={container.props.width}
						height={container.props.height}
						mobileFallback={container.props.mobileFallback}
						useContainer={container.props.useContainer ?? true}
						closingStrategy={"emptyClick"}
						useCloseButton={container.props.useCloseButton}
					/>,
				);
			} else if (isAnchor2dContainer(container)) {
				anchors.push(
					<ViewportAnchor2d
						key={JSON.stringify(container)}
						id={container.props.id}
						location={container.props.location}
						justification={container.props.justification}
						allowPointerEvents={
							container.props.allowPointerEvents ?? true
						}
						element={
							<AppBuilderContainerComponent
								namespace={namespace}
								{...container}
							/>
						}
						previewIcon={container.props.previewIcon}
						draggable={container.props.draggable}
						width={container.props.width}
						height={container.props.height}
						mobileFallback={container.props.mobileFallback}
						useContainer={container.props.useContainer ?? true}
						closingStrategy={"button"}
					/>,
				);
			}
		});
		setAnchors(anchors);
	}, [containers, props.namespace]);

	return anchors;
}
